import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { GraduationCap, BookOpen, ChevronRight, CheckCircle2, Clock } from 'lucide-react';

/**
 * ClientCourses — student-facing "My Courses" dashboard. Only shows
 * courses the client has been explicitly enrolled in by an admin or
 * trainer; browsing the whole catalog is intentionally not offered
 * because our LMS is used for assigned curriculum, not pay-per-course
 * self-signup.
 */
export default function ClientCourses() {
  const [rows, setRows] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/my/courses').then(r => setRows(r.data || [])).catch(() => setRows([]));
  }, []);

  if (rows === null) {
    return <div className='p-6 text-sm text-slate-500 dark:text-slate-400'>Loading your courses…</div>;
  }

  return (
    <div className='p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto' data-testid='client-courses-page'>
      <div className='mb-6'>
        <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-[#C8102E]'>Learn</div>
        <h1 className='font-display text-3xl font-bold text-slate-900 dark:text-slate-50 mt-1'>My Courses</h1>
        <p className='text-slate-500 dark:text-slate-400 text-sm mt-1'>Pick up where you left off — your trainer assigned these just for you.</p>
      </div>

      {rows.length === 0 ? (
        <Card className='p-10 text-center border border-slate-200 dark:border-slate-800' data-testid='client-courses-empty'>
          <GraduationCap className='w-10 h-10 mx-auto mb-3 text-slate-300 dark:text-slate-600' />
          <div className='font-semibold text-slate-700 dark:text-slate-200'>No courses yet</div>
          <div className='text-sm text-slate-500 dark:text-slate-400 mt-1'>
            Once your trainer enrolls you, your lessons and worksheets will appear here.
          </div>
        </Card>
      ) : (
        <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-4'>
          {rows.map(c => (
            <Card
              key={c.id}
              className='p-5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:shadow-md transition-shadow flex flex-col'
              data-testid={`client-course-card-${c.id}`}
            >
              <div className='flex items-start justify-between gap-2 mb-2'>
                <h3 className='font-display font-semibold text-slate-900 dark:text-slate-50 leading-tight'>{c.title}</h3>
                {c.progress_pct === 100 ? (
                  <Badge className='bg-[#167A5A] text-white'><CheckCircle2 className='w-3 h-3 mr-1' /> Done</Badge>
                ) : c.progress_pct > 0 ? (
                  <Badge className='bg-[#1B3A87] text-white'><Clock className='w-3 h-3 mr-1' /> In progress</Badge>
                ) : (
                  <Badge variant='outline' className='text-slate-600 dark:text-slate-300'>Not started</Badge>
                )}
              </div>
              <p className='text-xs text-slate-500 dark:text-slate-400 line-clamp-3 mb-3'>{c.description || 'No description.'}</p>

              <div className='text-xs text-slate-500 dark:text-slate-400 flex items-center gap-2 mb-2'>
                <BookOpen className='w-3.5 h-3.5' />
                <span data-testid={`client-course-progress-${c.id}`}>
                  {c.completed_count}/{c.lesson_count} lessons
                </span>
                <span className='ml-auto font-semibold text-slate-700 dark:text-slate-200'>{c.progress_pct}%</span>
              </div>
              <Progress value={c.progress_pct} className='h-1.5 mb-4' />

              <div className='mt-auto'>
                <Button
                  size='sm'
                  onClick={() => navigate(`/client/courses/${c.id}`)}
                  className='w-full bg-[#1B3A87] hover:bg-[#16306F] text-white'
                  data-testid={`client-course-open-${c.id}`}
                >
                  {c.progress_pct > 0 ? 'Continue' : 'Start course'} <ChevronRight className='w-3.5 h-3.5 ml-1' />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
